import {
  Objective,
  ObjectiveHierarchyType,
  ObjectiveScopeData,
  ObjectiveTree,
} from "@examspark/types";

export const parseDocumentSortKey = (sk: string) => {
  const [type, authority, publication, document] = sk.split("#");
  const [documentId, documentYear] = document.split(":");

  return {
    type,
    authority,
    publication,
    document,
    documentId,
    documentYear,
  };
};

/**
 * Ensure we're getting document scope from either a document-specific scope or
 * a section-specific scope.
 *
 * Example: `tea#teks#ma:2012#3` returns `tea#teks#ma:2012`
 */
export const parseObjectiveScope = (scope: string) => {
  const parts = scope.split("#");

  return {
    authority: parts[0],
    publication: parts[1],
    documentScope: parts.length >= 3 ? parts.slice(0, 3).join("#") : undefined,
    documentId: parts[2],
    sectionScope: parts.length === 4 ? parts.join("#") : undefined,
    sectionId: parts[3],
  };
};

/**
 * Ensure we're getting document scope from either a document-specific scope or
 * a section-specific scope.
 *
 * Example: `tea#teks#ma:2012#3` returns `tea#teks#ma:2012`
 */
export const getDocumentScopeFromGenericScope = (scope: string) => {
  const defaultDocumentParts = scope.split("#");
  return defaultDocumentParts.length === 4
    ? defaultDocumentParts.slice(0, -1).join("#")
    : defaultDocumentParts.join("#");
};

/**
 * Ensure we're getting section scope from either a section-specific scope or
 * `undefined`.
 *
 * Example: `tea#teks#ma:2012#3` returns `tea#teks#ma:2012#3`
 */
export const getSectionScopeFromGenericScope = (scope: string) => {
  const defaultDocumentParts = scope.split("#");
  if (defaultDocumentParts.length !== 4) return;
  return defaultDocumentParts.join("#");
};

export const getDocumentScopeFromScopeData = (
  scopeData: ObjectiveScopeData,
) => {
  const { authority, publication, document } = scopeData;
  return `${authority}#${publication}#${document}`;
};

export const getSectionScopeFromScopeData = (scopeData: ObjectiveScopeData) => {
  const { authority, publication, document, section } = scopeData;
  return `${authority}#${publication}#${document}#${section}`;
};

export const parseObjectiveSectionSortKey = (sk: string) => {
  const [type, authority, publication, document, section] = sk.split("#");
  const [documentId, documentYear] = document.split(":");

  return {
    type,
    authority,
    publication,
    document,
    documentId,
    documentYear,
    section,
  };
};

/**
 * Gets all the ascendant scopes for a given scope
 * For example, if called with scope "tea#teks#ma:2012#3", will return:
 * ["tea#teks#ma:2012#3", "tea#teks#ma:2012", "tea#teks", "tea"]
 * Optionally, can be limited to a particular depth, e.g. calling with depth=3 will return:
 * ["tea#teks#ma:2012#3", "tea#teks#ma:2012"]
 */
export const getAscendantObjectiveScopes = (scope: string, depth?: number) => {
  if (scope == null) return [];

  const results: string[] = [];
  const parts = scope.split("#");
  for (let i = parts.length; i > (depth ?? 0); i--) {
    results.push(parts.slice(0, i).join("#"));
  }
  return results;
};

export function getObjectiveHierarchyType(
  objective: Objective,
): ObjectiveHierarchyType;
export function getObjectiveHierarchyType(
  objective: Objective | undefined,
): ObjectiveHierarchyType | undefined;
export function getObjectiveHierarchyType(objective: Objective | undefined) {
  if (objective === undefined) return undefined;
  if (objective.sk === "authority") return "authority";
  if (objective.sk === "standard") return undefined;
  const [result] = objective.sk.split("#");
  return result as ObjectiveHierarchyType;
}

export const getHierarchyHeight = (
  type: ObjectiveHierarchyType | undefined,
) => {
  switch (type) {
    case "authority":
      return 4;
    case "publication":
      return 3;
    case "document":
      return 2;
    case "section":
      return 1;
    case undefined:
      return 0;
  }
};

export const doesHierarchyTypeInclude = (
  type: ObjectiveHierarchyType,
  otherType: ObjectiveHierarchyType,
) => {
  return getHierarchyHeight(type) >= getHierarchyHeight(otherType);
};

export function flattenObjectivesTree<T extends Objective>(
  objectives: Array<ObjectiveTree<T>>,
  rootType: ObjectiveHierarchyType | undefined,
  leafType?: ObjectiveHierarchyType,
): Objective[];
export function flattenObjectivesTree<T extends Objective>(
  objectives: Array<ObjectiveTree<T>> | undefined,
  rootType: ObjectiveHierarchyType | undefined,
  leafType?: ObjectiveHierarchyType,
): Objective[] | undefined;
export function flattenObjectivesTree<T extends Objective>(
  objectives: Array<ObjectiveTree<T>> | undefined,
  rootType: ObjectiveHierarchyType | undefined,
  leafType?: ObjectiveHierarchyType,
) {
  if (objectives === undefined || rootType === undefined) return undefined;
  if (objectives.length === 0) return [];

  const depth = getHierarchyHeight(rootType) - getHierarchyHeight(leafType);
  let result = objectives;

  for (let i = 0; i < depth; i++) {
    result = result.flatMap((o) => o.children ?? []);
  }

  return result.map((o) => o.objective);
}
